/**
 * Pure navigation-state transitions for the file browser.
 *
 * STD-029 R-10: navigation state names the *node*, not a path — and since B-4 it
 * names **only** the node. The `FolderRef` is one field, because the two things
 * that kept the path half alive are gone: the listing is
 * `GET /api/file-objects/{id}/children` and the writes are id-addressed.
 *
 * What that buys is not tidiness. A path is a coordinate inside one
 * organization's own tree, so while navigation state carried one, a folder
 * another organization shared had no state the browser could hold for it — which
 * is D-6, and why "open a shared folder" was unimplementable rather than merely
 * unimplemented.
 *
 * Kept free of React so the transitions are unit-testable in the node-environment
 * vitest suite — this app ships no jsdom/RTL, so a rule left inside a hook has no
 * tier that can hold it.
 */
import type { ChildrenListResponse, FolderNode } from '../../types/api';
import type { FileItem, FolderRef } from './types';

/**
 * The caller's own organization root, before any listing has named it.
 *
 * `id: null` is a real value with a defined meaning — "my organization's root",
 * resolved server-side from the JWT — and not an absence. `/root/children`
 * answers with the row's actual id, so after the first listing the browser holds
 * that instead.
 */
export const ROOT_FOLDER: FolderRef = { id: null };

/**
 * Open a folder entry.
 *
 * Every folder in a children listing is a registry row, so it always has an id.
 * The `undefined` case this used to carry — a folder synthesised from a flight's
 * key — cannot arise: R-9 entries are files, they arrive in their own array, and
 * nothing synthesises a folder any more.
 */
export function folderRefForEntry(file: Pick<FileItem, 'object_id'>): FolderRef {
  return { id: file.object_id ?? null };
}

/**
 * Is this navigation target the folder the browser is ALREADY listing?
 *
 * Asked of the LISTING rather than of navigation state, and that is the whole
 * point: the two can name one folder in two spellings. At the caller's own root
 * the state holds `{id: null}` — the alias the server resolves from the JWT —
 * while the Home crumb, built from the resolved listing, carries the root's real
 * row id. Clicking Home there is not a navigation, but an id comparison in the
 * store cannot see that (`realRootId !== null`), so it pushed a history entry
 * and switched the query key, costing a refetch and an extra Back press for the
 * rest of the session. The listing knows, because it carries the node.
 *
 * `target.id === null` is deliberately **false**: `null` means "resolve my root
 * for me", and answering it from a listing whose node happens to be that root
 * would make the alias and the id interchangeable in a place where the whole
 * B-5 design keeps them distinct.
 *
 * Safe against a stale listing by construction: `useFolderChildrenQuery` sets no
 * `placeholderData`, so `listing` is `undefined` while a new key loads and an
 * old node can never shadow a click.
 */
export function isCurrentNode(
  response: ChildrenListResponse | undefined,
  target: FolderRef
): boolean {
  return target.id !== null && response?.node?.object_id === target.id;
}

/**
 * One breadcrumb.
 *
 * `isRoot` is not "index 0" — it is "this node is the caller's OWN organization
 * root", which the server states by giving it a null `parent_id`. Inside a share
 * the topmost visible node has a real parent (the granter's folder, which the
 * caller may not see), so no crumb is a root and no Home crumb is rendered. That
 * is the disclosure rule, not a styling choice: offering "Home" from inside
 * somebody else's subtree would be a navigation that silently changes
 * organization.
 */
export interface Crumb {
  ref: FolderRef;
  label: string;
  isRoot: boolean;
}

/**
 * The breadcrumb trail for a listing: `[...ancestors, node]`, topmost first.
 *
 * `current` is the fallback while nothing has loaded. It matters on the very
 * first render and after every navigation: without it the bar would flicker
 * empty, and at the root that means the Home crumb — the browser's only way back
 * — briefly does not exist.
 *
 * An organization with no registry row at all (`node: null`) gets the single
 * root crumb, so its empty Files tab still has a Home to sit on and its "New
 * folder" button still has a parent to send (`null`).
 */
export function crumbRefs(
  response: ChildrenListResponse | undefined,
  current: FolderRef = ROOT_FOLDER
): Crumb[] {
  if (!response || !response.node) {
    // Nothing loaded, or an organization whose tree has never been created. At
    // the root we can still name the crumb; anywhere else we hold no names at
    // all and rendering a guess would be worse than rendering nothing.
    return current.id === null ? [{ ref: ROOT_FOLDER, label: '', isRoot: true }] : [];
  }
  const chain: FolderNode[] = [...response.ancestors, response.node];
  const ownRoot = chain[0].parent_id === null;
  return chain.map((node, index) => ({
    ref: { id: node.object_id },
    label: node.name,
    isRoot: ownRoot && index === 0,
  }));
}
